import React from 'react';
import { CheckCircle, Clock, Sparkles, TrendingUp, Info } from 'lucide-react';
import OverlayCoverageList from '../VendorOverlayPicker/OverlayCoverageList';
import SpendDrillButton from '../components/SpendDrillButton';
import { selectIndicator, selectVendorIndicator } from '../hooks/useSpendIndicators';
import { POLICY_SIGNALS } from '../config/ztaArchitectureData';

// Format date for display (returns null if invalid)
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Build tooltip content for suggestion
const buildSuggestionTooltip = (suggestion) => {
  if (!suggestion?.detected?.length) return '';
  const top = suggestion.detected[0];
  const lastPurchaseDate = formatDate(top.lastPurchase);
  const lines = [
    `Based on Purchase History`,
    `───────────────────`,
    `Vendor: ${top.vendor}`,
    top.product ? `Product: ${top.product}` : null,
    `Spend: $${Math.round(top.spend).toLocaleString()}`,
    `Transactions: ${top.transactions}`,
    lastPurchaseDate ? `Last Purchase: ${lastPurchaseDate}` : null,
    suggestion.detected.length > 1 ? `───────────────────` : null,
    suggestion.detected.length > 1 ? `+ ${suggestion.detected.length - 1} more related products` : null
  ].filter(Boolean);
  return lines.join('\n');
};

const ZtaSignalsRow = ({
  selectedDepartment,
  getSignalState,
  getSuggestion,
  suggestionModeEnabled,
  coverageMap,
  effectiveSpendIndicators,
  pdpData,
  setEditingSignal,
  setEditingPDP,
  openSignalDrillIn,
  openVendorDrillIn,
}) => {
  // Both signal columns render the identical card — the two map calls below
  // feed it the first three and the remaining signals respectively.
  const renderSignalCard = (signal) => {
    const state = getSignalState(signal.id);
    const hasData = Object.keys(state).length > 0 && state.vendor;
    const suggestion = getSuggestion(signal.id);
    // Only show suggestions when suggestion mode is enabled
    const hasSuggestion = suggestionModeEnabled && suggestion?.detected?.length > 0 && !hasData;

    // Vendor overlay coverages — array of { vendor, productName, coverageLevel, notes }
    // sorted strongest first. Empty when no vendors selected or none cover this signal.
    const overlayCoverages = coverageMap[signal.id] || [];
    const hasOverlayCoverage = overlayCoverages.length > 0;
    const primaryOverlay = overlayCoverages[0];

    // Calculate readiness percentage based on assessment responses
    const calculateReadiness = () => {
      if (!state.assessmentResponses) return null;
      const responses = state.assessmentResponses;
      let score = 0;
      let total = 0;
      signal.assessmentQuestions.forEach(q => {
        if (responses[q.id]) {
          total++;
          if (q.type === 'choice') {
            if (responses[q.id] === 'Yes') score += 100;
            else if (responses[q.id] === 'Partial') score += 50;
          } else if (q.type === 'percentage') {
            score += parseInt(responses[q.id]) || 0;
          }
        }
      });
      return total > 0 ? Math.round(score / total) : null;
    };
    const readiness = calculateReadiness();

    return (
      <button
        key={signal.id}
        className={`zta-signal-card ${hasData ? 'zta-signal-card--configured' : ''} ${hasSuggestion ? 'zta-signal-card--has-suggestion' : ''} ${hasOverlayCoverage ? 'zta-signal-card--overlay-active' : ''}`}
        onClick={() => setEditingSignal(signal)}
        data-overlay-count={hasOverlayCoverage ? overlayCoverages.length : undefined}
        data-overlay-product={primaryOverlay?.productName}
        data-coverage-level={primaryOverlay?.coverageLevel}
      >
        {hasOverlayCoverage && (
          <OverlayCoverageList
            coverages={overlayCoverages}
            position="top"
            spendIndicators={effectiveSpendIndicators}
            onDrillIn={(v) => openVendorDrillIn({
              ...v,
              scope: 'signal',
              code: signal.id,
              name: `${signal.label} · ${v.product || v.vendor}`,
            })}
          />
        )}
        {hasSuggestion && (
          <div className="zta-suggestion-indicator" title={buildSuggestionTooltip(suggestion)}>
            <Sparkles size={12} />
          </div>
        )}
        <div className="zta-signal-label">
          {signal.label}
          {selectedDepartment && effectiveSpendIndicators && (
            <SpendDrillButton
              indicator={selectIndicator(effectiveSpendIndicators, 'signals', signal.id)}
              onClick={() => openSignalDrillIn(signal)}
              label={`${signal.label} transactions`}
              size={12}
              className="zta-spend-drill--compact"
            />
          )}
        </div>
        {hasData ? (
          <>
            <div className="zta-signal-vendor-name">
              {state.product || state.vendor}
              {(() => {
                const ind = selectVendorIndicator(effectiveSpendIndicators, state.vendor, state.product);
                return ind ? (
                  <SpendDrillButton
                    indicator={ind}
                    onClick={() => openVendorDrillIn({
                      vendor: state.vendor,
                      product: state.product || null,
                      scope: 'signal',
                      code: signal.id,
                      name: `${signal.label} · ${state.product || state.vendor}`,
                    })}
                    label={`${state.vendor} transactions`}
                    size={10}
                    className="zta-spend-drill--tiny"
                  />
                ) : null;
              })()}
            </div>
            <div className="zta-signal-vendor-sub">
              {state.product ? state.vendor : ''}
            </div>
            <div className="zta-signal-badges">
              {state.integrationStatus === 'fully_integrated' && (
                <span className="zta-signal-badge zta-signal-badge--success">
                  <CheckCircle size={12} /> Integrated
                </span>
              )}
              {state.integrationStatus === 'partial' && (
                <span className="zta-signal-badge zta-signal-badge--warning">
                  <Clock size={12} /> Partial
                </span>
              )}
              {readiness !== null && (
                <span className={`zta-signal-badge ${readiness >= 75 ? 'zta-signal-badge--success' : readiness >= 50 ? 'zta-signal-badge--warning' : 'zta-signal-badge--danger'}`}>
                  {readiness}%
                </span>
              )}
            </div>
          </>
        ) : hasSuggestion ? (
          <>
            <div className="zta-signal-suggestion">
              <span className="zta-suggestion-vendor">{suggestion.topVendor}</span>
              {suggestion.topProduct && (
                <span className="zta-suggestion-product">{suggestion.topProduct}</span>
              )}
              <span
                className="zta-suggestion-info"
                title={buildSuggestionTooltip(suggestion)}
                onClick={(e) => e.stopPropagation()}
              >
                <Info size={12} />
              </span>
            </div>
            <div className="zta-suggestion-meta">
              <TrendingUp size={10} />
              <span>
                ${Math.round((suggestion.totalSpend || 0) / 1000)}k
                {formatDate(suggestion.detected?.[0]?.lastPurchase)
                  ? ` • ${formatDate(suggestion.detected?.[0]?.lastPurchase)}`
                  : ` • ${suggestion.confidence}% match`
                }
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="zta-signal-question">{signal.question}</div>
            <div className="zta-signal-vendors">{signal.defaultVendors}</div>
          </>
        )}
      </button>
    );
  };

  return (
    <section className="zta-layer zta-policy-decision">
      <div className="zta-layer-label">POLICY<br/>DECISION</div>
      <div className="zta-pdp-container">
        <div className="zta-signals">
          {POLICY_SIGNALS.slice(0, 3).map(renderSignalCard)}
        </div>

        <div className="zta-pdp-center">
          <button
            className={`zta-pdp-box ${pdpData.vendor ? 'zta-pdp-box--configured' : ''}`}
            onClick={() => setEditingPDP(true)}
          >
            <div className="zta-pdp-title">
              POLICY DECISION POINT (PDP)
              {selectedDepartment && effectiveSpendIndicators && (
                <SpendDrillButton
                  indicator={selectIndicator(effectiveSpendIndicators, 'signals', 'pdp')}
                  onClick={() => openSignalDrillIn({ id: 'pdp', label: 'PDP' })}
                  label="PDP transactions"
                  size={12}
                  className="zta-spend-drill--compact"
                />
              )}
            </div>
            {pdpData.vendor ? (
              <>
                <div className="zta-pdp-vendor">
                  {pdpData.product || pdpData.vendor}
                  {(() => {
                    const ind = selectVendorIndicator(effectiveSpendIndicators, pdpData.vendor, pdpData.product);
                    return ind ? (
                      <SpendDrillButton
                        indicator={ind}
                        onClick={() => openVendorDrillIn({
                          vendor: pdpData.vendor,
                          product: pdpData.product || null,
                          scope: 'signal',
                          code: 'pdp',
                          name: `PDP · ${pdpData.product || pdpData.vendor}`,
                        })}
                        label={`${pdpData.vendor} transactions`}
                        size={10}
                        className="zta-spend-drill--tiny"
                      />
                    ) : null;
                  })()}
                </div>
                <div className="zta-pdp-vendor-sub">{pdpData.product ? pdpData.vendor : ''}</div>
                {pdpData.integrationStatus && (
                  <div className="zta-pdp-badges">
                    {pdpData.integrationStatus === 'fully_integrated' && (
                      <span className="zta-signal-badge zta-signal-badge--success">
                        <CheckCircle size={12} /> Integrated
                      </span>
                    )}
                    {pdpData.integrationStatus === 'partial' && (
                      <span className="zta-signal-badge zta-signal-badge--warning">
                        <Clock size={12} /> Partial
                      </span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="zta-pdp-subtitle">Continuous Trust Evaluation Engine</div>
                <div className="zta-pdp-example">e.g. Okta Verify / Azure AD Identity Protection</div>
              </>
            )}
          </button>
          <div className="zta-trust-arrow">
            <span>TRUST DECISION</span>
            <div className="zta-arrow-down"></div>
          </div>
        </div>

        <div className="zta-signals">
          {POLICY_SIGNALS.slice(3).map(renderSignalCard)}
        </div>
      </div>
    </section>
  );
};

export default ZtaSignalsRow;
